import { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid2,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import AlertPanel from "./AlertPanel";
import { AlertAreaStatus } from "./AlertAreaStatus";
import { MainData } from "IF/interfaceType";

import { createTitle, formatDate } from "Utils/format";
import { DataContext } from "Contexts/DataProvider";
import { UserRole, useSessionStorage } from "IF/userSession";
import { useThemeMode } from "Contexts/ThemeContext";
import { ViewContext } from "Contexts/ViewProvider";

export type displayPeriod = {
  startDate: string; // 表示期間開始日
  endDate: string; // 表示期間終了日
};

export const Alert = ({ data }: { data: MainData }) => {
  const { getUserData } = useSessionStorage();
  const userData = getUserData();
  const { userRole } = userData;

  // レイアウト設定
  const [tableBodyHeight, setTableBodyHeight] = useState(600);
  const [xsWidth, setXsWidth] = useState(0);

  const theme = useTheme();
  const { mode } = useThemeMode();

  // serialNo単位、エリア名単位切替用
  const context = useContext(ViewContext);
  if (!context) {
    throw new Error("ChildComponent must be used within an ViewProvider");
  }
  const { isName, setVisible } = context;

  // サイドバーデータ持ち回り
  const sidebarContext = useContext(DataContext);
  if (!sidebarContext) {
    throw new Error("ChildComponent must be used within an ViewProvider");
  }
  const { menuItems, setChoiceMenu } = sidebarContext;

  //　表示期間
  const [periods, setPeriods] = useState<Record<string, displayPeriod>>({});

  //　表示期間変更
  const handlePeriodChange = (
    simId: string,
    field: keyof displayPeriod,
    value: string
  ) => {
    setPeriods((prev) => {
      // 現在の対象 simId の表示期間を取得
      const currentPeriod = prev[simId] || { startDate: "", endDate: "" };

      // 更新後の startDate と endDate を仮に決定する
      let newStartDate = currentPeriod.startDate;
      let newEndDate = currentPeriod.endDate;

      if (field === "startDate") {
        newStartDate = value;
      } else if (field === "endDate") {
        newEndDate = value;
      }

      // 両方の値が設定されている場合、日付の整合性をチェックする
      if (newStartDate && newEndDate) {
        const start = new Date(newStartDate);
        const end = new Date(newEndDate);
        if (start > end) {
          alert("開始日は終了日より後の日付にできません。");
          return prev;
        }
      }

      // 整合性が問題なければ更新を反映
      return {
        ...prev,
        [simId]: { ...currentPeriod, [field]: value },
      };
    });
  };

  const showData = useMemo(() => {
    const sourceData = isName ? data.areaNameDataList : data.serialNoDataList;

    // まだperiodsがsourceDataに対して初期化されていない場合は、計算を保留
    const isReady = sourceData.every((panel) => periods[panel.simId]);
    if (!isReady) {
      return null; // データが準備できていないことを示す
    }

    // 各パネルのalertDataListをフィルタリング
    return sourceData.map((panel) => {
      const period = periods[panel.simId];
      const filteredAlerts = panel.alertDataList.filter((worker) => {
        if (!worker.alertTime) return false;
        const alertDate = new Date(worker.alertTime);
        return (
          alertDate >= new Date(period.startDate + " 00:00:00") &&
          alertDate <= new Date(period.endDate + " 23:59:59")
        );
      });
      return { ...panel, alertDataList: filteredAlerts };
    });
  }, [isName, data, periods]);

  const today = new Date();

  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1); // 明日の日付を設定

  // 詳細画面用（スマホ・タブレット）
  // クリックされたエリアのインデックスを設定
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // 選択パネル切替
  const handleAreaStatusClick = (index: number) => {
    setSelectedIndex(index);
    setVisible(false);
  };
  // 詳細画面→一覧に戻る
  const handleReturn = () => {
    setSelectedIndex(null);
    setVisible(true);
  };

  // タイトル設定
  useEffect(() => {
    if (menuItems.length > 2) {
      const index = UserRole.YKC !== userRole ? 1 : 2;
      document.title = createTitle(menuItems[index].text);
      setChoiceMenu(menuItems[index].text);
    }
  }, [userRole, menuItems, setChoiceMenu]);

  // 初期表示時処理
  useEffect(() => {
    const updateHeight = () => {
      const windowHeight = window.innerHeight - 112;
      setTableBodyHeight(windowHeight);
    };

    const updateXsWidth = () => {
      const windowWidth = document.documentElement.clientWidth - 48;
      setXsWidth(windowWidth);
    };
    // 初期表示時とリサイズ時に計算
    updateHeight();
    window.addEventListener("resize", updateHeight);

    updateXsWidth();
    window.addEventListener("resize", updateXsWidth);

    setVisible(true);

    // クリーンアップ
    return () => {
      window.removeEventListener("resize", updateHeight);
      window.removeEventListener("resize", updateXsWidth);
    };
  }, [setVisible]);

  useEffect(() => {
    const today = formatDate(new Date());
    const sourceData = isName ? data.areaNameDataList : data.serialNoDataList;
    const updatedPeriods = { ...periods };
    let periodsChanged = false;

    sourceData.forEach((panel) => {
      if (!updatedPeriods[panel.simId]) {
        updatedPeriods[panel.simId] = {
          startDate: today,
          endDate: today,
        };
        periodsChanged = true;
      }
    });

    if (periodsChanged) {
      setPeriods(updatedPeriods);
    }
  }, [isName, data]);

  if (!showData) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {/* PC 表示 (md以上) */}
      <Box sx={{ display: { xs: "none", sm: "none", md: "block" } }}>
        <Box
          sx={{
            display: "flex",
            height: "100%",
            overflowX: "auto",
            gap: 1,
          }}
        >
          {showData.map((panel, index) => (
            <Box
              key={index}
              sx={{
                flexGrow: 1,
                minWidth: {
                  md: "calc(48% - 10px)",
                  lg: "calc(32% - 10px)",
                },
                maxWidth: {
                  md: "calc(48% - 10px)",
                  lg: "calc(32% - 10px)",
                },
                bgcolor: theme.palette.custom.panel,
                height: tableBodyHeight,
              }}
            >
              {/* 表示期間設定フォーム（各パネルごと） */}
              {/*
                            <Box
                sx={{
                  width: "280px",
                  display: "flex",
                  justifyContent: "flex-start",
                  mt: "15px",
                  ml: "10px",
                  gap: 1.5,
                }}
              >

              */}

              <Box
                sx={{
                  width: { xs: "100%", md: "360px" }, // 280→360 くらいに
                  minWidth: { md: "360px" },
                  flexShrink: 0,                     // ここ重要：潰れない
                  display: "flex",
                  justifyContent: "flex-start",
                  mt: "15px",
                  ml: "10px",
                  gap: 1.5,
                }}
              >

                <TextField
                  label="表示期間開始日"
                  type="date"
                  name="startDate"
                  value={periods[panel.simId]?.startDate || ""}
                  onChange={(e) =>
                    handlePeriodChange(panel.simId, "startDate", e.target.value)
                  }
                  sx={{
                    input: {
                      fontSize: "0.7rem",
                      fontWeight: "bold",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: mode === "dark" ? "white" : "black",
                      },
                      ...(mode === "dark" && {
                        "&::-webkit-calendar-picker-indicator": {
                          filter: "invert(1)",
                        },
                      }),
                    },
                  }}
                  InputLabelProps={{
                    shrink: true,
                    sx: { fontSize: "0.7rem", fontWeight: "bold" },
                  }}
                  InputProps={{
                    sx: {
                      fontSize: "0.7rem",
                      height: "30px",
                      padding: "4px",
                      fontWeight: "bold",
                    },
                  }}
                  size="small"
                  fullWidth
                />
                <Box sx={{ display: "flex", alignItems: "center" }}>～</Box>
                <TextField
                  label="表示期間終了日"
                  type="date"
                  name="endDate"
                  value={periods[panel.simId]?.endDate || ""}
                  onChange={(e) =>
                    handlePeriodChange(panel.simId, "endDate", e.target.value)
                  }
                  sx={{
                    input: {
                      fontSize: "0.7rem",
                      fontWeight: "bold",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: mode === "dark" ? "white" : "black",
                      },
                      ...(mode === "dark" && {
                        "&::-webkit-calendar-picker-indicator": {
                          filter: "invert(1)",
                        },
                      }),
                    },
                  }}
                  InputLabelProps={{
                    shrink: true,
                    sx: { fontSize: "0.7rem", fontWeight: "bold" },
                  }}
                  InputProps={{
                    sx: {
                      fontSize: "0.7rem",
                      height: "30px",
                      padding: "4px",
                      fontWeight: "bold",
                    },
                  }}
                  size="small"
                  fullWidth
                />
              </Box>
              <AlertPanel panel={panel} />
            </Box>
          ))}
        </Box>
      </Box>
      {/* スマホ・タブレット */}
      <Box sx={{ display: { xs: "block", sm: "block", md: "none" } }}>
        {selectedIndex === null ? (
          <Grid2 container spacing={1}>
            {showData.map((panel, index) => (
              <Grid2 size={{ xs: 12, sm: 6 }} key={index}>
                <Card
                  sx={{
                    flexGrow: 1,
                    bgcolor: theme.palette.custom.panel,
                    padding: "8px",
                    overflow: "hidden",
                    Width: xsWidth,
                  }}
                >
                  <CardContent sx={{ p: 0.5 }}>
                    <Box
                      sx={{
                        width: "310px",
                        display: "flex",
                        justifyContent: "flex-start",
                        marginTop: "15px",
                        marginBottom: "8px",
                        gap: 0.5,
                      }}
                    >
                      {/* 出力期間 */}
                      <Grid2 size={{ xs: 5, lg: 5 }}>
                        <TextField
                          size="small"
                          label="表示期間開始日"
                          type="date"
                          name="startDate"
                          value={periods[panel.simId]?.startDate || ""}
                          onChange={(e) =>
                            handlePeriodChange(
                              panel.simId,
                              "startDate",
                              e.target.value
                            )
                          }
                          sx={{
                            input: {
                              fontSize: "0.7rem",
                              fontWeight: "bold",
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor:
                                  mode === "dark" ? "white" : "black",
                              },
                              ...(mode === "dark" && {
                                "&::-webkit-calendar-picker-indicator": {
                                  filter: "invert(1)",
                                },
                              }),
                            },
                          }}
                          InputLabelProps={{
                            shrink: true,
                            sx: {
                              fontSize: "0.7rem",
                              fontWeight: "bold",
                            },
                          }}
                          InputProps={{
                            sx: {
                              fontSize: "0.7rem",
                              height: "30px",
                              fontWeight: "bold",
                            },
                          }}
                          fullWidth
                        />
                      </Grid2>
                      <Grid2
                        sx={{ alignContent: "center" }}
                        size={{ xs: 1, lg: 1 }}
                      >
                        ～
                      </Grid2>
                      <Grid2 size={{ xs: 5, lg: 5 }}>
                        <TextField
                          size="small"
                          label="表示期間終了日"
                          type="date"
                          name="endDate"
                          value={periods[panel.simId]?.endDate || ""}
                          onChange={(e) =>
                            handlePeriodChange(
                              panel.simId,
                              "endDate",
                              e.target.value
                            )
                          }
                          sx={{
                            input: {
                              fontSize: "0.7rem",
                              fontWeight: "bold",
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor:
                                  mode === "dark" ? "white" : "black",
                              },
                              ...(mode === "dark" && {
                                "&::-webkit-calendar-picker-indicator": {
                                  filter: "invert(1)",
                                },
                              }),
                            },
                          }}
                          InputLabelProps={{
                            shrink: true,
                            sx: { fontSize: "0.7rem", fontWeight: "bold" },
                          }}
                          InputProps={{
                            sx: {
                              fontSize: "0.7rem",
                              height: "30px",
                              fontWeight: "bold",
                            },
                          }}
                          fullWidth
                        />
                      </Grid2>
                    </Box>
                    <Box onClick={() => handleAreaStatusClick(index)}>
                      <AlertAreaStatus panel={panel} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid2>
            ))}
          </Grid2>
        ) : (
          // 詳細画面
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  bgcolor: theme.palette.custom.panel,
                  padding: "3px",
                }}
              >
                <Typography fontWeight="fontWeightBold">
                  ＞他エリア状況
                </Typography>
                <Box sx={{ height: "50px", overflowY: "auto" }}>
                  {showData.map(
                    (row, index) =>
                      selectedIndex !== index && (
                        <Typography fontWeight="fontWeightRegular" key={index}>
                          {row.name}
                          <span
                            style={{
                              color:
                                row.alertDataList.length > 0
                                  ? "red"
                                  : "default",
                            }}
                          >
                            {` ${row.alertDataList.length}回`}
                          </span>
                        </Typography>
                      )
                  )}
                </Box>
              </Box>
              <Box
                sx={{
                  flexGrow: 1,
                  bgcolor: theme.palette.custom.panel,
                  minHeight: tableBodyHeight - 100,
                }}
              >
                <AlertPanel panel={showData[selectedIndex]} minusHeight={60} />
              </Box>
              <Box sx={{ textAlign: "end", verticalAlign: "bottom" }}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    width: "100px",
                  }}
                  onClick={handleReturn}
                >
                  戻る
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </>
  );
};

export default Alert;
